// ─── src/autoLoginReporter.ts ───────────────────────────────────────────
/**
 * Fires a LOGIN xAPI statement (MoE spec) to recordLrsStatementLive.php
 * immediately when the bundle executes.
 */
(() => {
  /* ------------------------------------------------------------------ */
  /*  Skip MoE reporting on localhost / non-production builds          */
  /* ------------------------------------------------------------------ */
  const hostname = window.location.hostname;
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '[::1]' ||
    hostname.endsWith('.local');
  const isProdBuild = process.env.NODE_ENV === 'production';

  if (!isProdBuild || isLocalHost) {
    console.info(
      '[AUTOLOGIN] Skipping MoE auto-login ping (environment not production or running on localhost)',
      { hostname, isProdBuild }
    );
    return;
  }

  const ENDPOINT =
    'https://www.yisumatica.org.il/yisumaticaApiZvi/recordLrsStatementLive.php';

  /* Grab whatever identifiers we can find synchronously */
  const userId =
    (window as any)?.__PRELOADED_STATE__?.session?.user?.id ||
    localStorage.getItem('userId') ||
    'anonymous';

  console.group('[AUTOLOGIN] Auto-login reporter - Building ENTER statement');
  console.log('[AUTOLOGIN] ========================================');
  console.log('[AUTOLOGIN] MoE Compliance Check');
  console.log('[AUTOLOGIN] ========================================');
  console.log('[AUTOLOGIN] User ID:', userId);

  /* Helper: Determine MoE identity type from userId */
  const getIdentityHomePage = (uid: string): string => {
    const isNumericId = /^\d+$/.test(uid);
    return isNumericId
      ? 'https://lxp.education.gov.il/xapi/moe/identity/idnumber'
      : 'https://lxp.education.gov.il/xapi/moe/identity/exidentifier';
  };

  /* Hash identity number for MoE (SHA-256) */
  const hashIdentityNumber = async (value: string): Promise<string> => {
    // Always hash the value (whether it's an ID number or email) for MoE compliance
    // MoE requires hashed identity, not plain text
    const encoder = new TextEncoder();
    const data = encoder.encode(value);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
    return hashHex;
  };

  /* Build MoE-compliant minimal statement for ENTER */
  const lmsId  = window.location.origin; // e.g. https://www.yisumatica.org.il
  const verbId = 'https://lxp.education.gov.il/xapi/moe/verbs/enter';
  const lmsType = 'https://lxp.education.gov.il/xapi/moe/lms'; // Fixed: removed /activities/
  
  // Determine identity type
  const identityHomePage = getIdentityHomePage(userId);
  const identityType = /^\d+$/.test(userId) ? 'idnumber' : 'exidentifier';

  console.log('[AUTOLOGIN] ========================================');
  console.log('[AUTOLOGIN] MoE Compliance Details:');
  console.log('[AUTOLOGIN]   ✓ LMS ID:', lmsId);
  console.log('[AUTOLOGIN]   ✓ MoE verb ID:', verbId);
  console.log('[AUTOLOGIN]   ✓ LMS activity type:', lmsType, '(should be /moe/lms, NOT /activities/lms)');
  console.log('[AUTOLOGIN]   ✓ Identity type:', identityType);
  console.log('[AUTOLOGIN]   ✓ Actor homePage:', identityHomePage);
  console.log('[AUTOLOGIN] ========================================');

  const traceId = `lrs-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Hash identity number if it's numeric (Israeli ID number)
  hashIdentityNumber(userId).then((hashedIdentity) => {
  const statement = {
    actor: { 
      objectType: 'Agent', 
      account: { 
        homePage: identityHomePage, // Fixed: MoE identity (idnumber or exidentifier)
          name: hashedIdentity // Fixed: hashed identity number instead of email
      } 
    },
    verb:  { id: verbId },
    object:{ 
      objectType: 'Activity', 
      id: lmsId, 
      definition: { 
          type: lmsType, // Fixed: /moe/lms instead of /moe/activities/lms
          name: { he: 'Yisumatica LMS' },
          description: { he: 'מערכת למידה Yisumatica' } // Fixed: describes the system, not the action
      } 
    },
    context: {
      contextActivities: {
        grouping: [
          {
            objectType: 'Activity',
            id: lmsId,
            definition: {
                type: lmsType, // Fixed: added definition.type
              name: { he: 'Yisumatica LMS' }
            }
          }
        ]
      },
      extensions: {
        'https://yisumatica.org.il/xapi/extensions/traceId': traceId
      }
    },
    timestamp: new Date().toISOString(),
    version: '1.0.3'
  };

  console.log('[AUTOLOGIN] ========================================');
  console.log('[AUTOLOGIN] Statement object (MoE-compliant):');
  console.log(JSON.stringify(statement, null, 2));
  console.log('[AUTOLOGIN] ========================================');

  const payload = {
    statement: JSON.stringify([statement]),
    verb: 'enter',
    appId: 'lms-shell',
    userId,
    traceId
  };

  console.log('[AUTOLOGIN] ========================================');
  console.log('[AUTOLOGIN] Payload to PHP:');
  console.log('[AUTOLOGIN]   verb:', payload.verb);
  console.log('[AUTOLOGIN]   appId:', payload.appId);
  console.log('[AUTOLOGIN]   statementLength:', payload.statement.length);
  console.log('[AUTOLOGIN]   traceId:', traceId);
  console.log('[AUTOLOGIN] ========================================');

  const body = JSON.stringify(payload);
  console.log('[AUTOLOGIN] Sending to:', ENDPOINT);

  /* 1️⃣  Try beacon (non-blocking, survives unload) */
  if (navigator.sendBeacon?.(ENDPOINT, body)) {
    console.log('[AUTOLOGIN] ✔ Sent via navigator.sendBeacon');
    console.log('[AUTOLOGIN] ✓ Ministry will receive MoE-compliant statement');
    console.groupEnd();
    return;
  }

  /* 2️⃣  Fallback to fetch POST */
  console.log('[AUTOLOGIN] Beacon not available, using fetch');
  fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body,
    keepalive: true,
  })
    .then((r) => {
      console.log('[AUTOLOGIN] ✔ Fetch response:', r.status, r.statusText);
      console.log('[AUTOLOGIN] ✓ Ministry will receive MoE-compliant statement');
      console.groupEnd();
    })
    .catch((e) => {
      console.error('[AUTOLOGIN] ✖ Fetch failed:', e);
      console.groupEnd();
      });
    });
})();
